import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { IoIosCloseCircle } from "react-icons/io";
import { FiUploadCloud, FiDownload } from "react-icons/fi";
import axios from 'axios';
import { Base_Url, csvuplode } from '../../../config/config';
import { toast } from 'react-toastify';
import { useMyContext } from '../../../context/MyContext';
import ConfirmBulkOrder from './ConfirmBulkOrder';
import Spinner from '../../spinner/spinner';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText,
    Sparkles,
    X,
    CheckCircle,
    Eye,
    AlertCircle,
    Download,
    Trash2,
    ArrowRight,
    Search,
} from "lucide-react";


const UploadBulk = ({ isopen, onClose }) => {
    const [file, setFile] = useState(null);
    const { isDarkMode } = useMyContext();
    const [reqBody, setReqBody] = useState([])
    const [loading, setloading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [csvParsed, setCsvParsed] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    const navigate = useNavigate();

    const handleViewClick = () => {
        setIsPopupOpen(true);
    };
    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const handlePreviewClick = () => {
        if (previewData.length > 0) {
            setShowPreview(true);
        }
    };

    const closePreviewModal = () => {
        setShowPreview(false);
    };

    const handleCloseBulk = () => {
        if (typeof onClose === 'function') {
            onClose();
        }
        setFile(null);
        setPreviewData([]);
        setCsvParsed(false);
        setIsDragging(false);
    };

    const parseCSV = (csvText) => {
        const parsed = Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true
        });
        return parsed.data;
    };

    const processSelectedFile = (selectedFile) => {
        if (
            selectedFile &&
            (selectedFile.type === 'text/csv' ||
             selectedFile.type.includes('csv') ||
             selectedFile.name.toLowerCase().endsWith('.csv') ||
             selectedFile.type === 'application/vnd.ms-excel')
        ) {
            setFile(selectedFile);

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csvData = parseCSV(e.target.result);
                    setPreviewData(csvData);
                    setCsvParsed(true);
                } catch (error) {
                    toast.error('Error parsing CSV file');
                    setCsvParsed(false);
                }
            };
            reader.readAsText(selectedFile);
        } else {
            toast.error('Please select a valid CSV file.');
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            processSelectedFile(selectedFile);
        }
        event.target.value = null;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processSelectedFile(e.dataTransfer.files[0]);
        }
    };

    const handleClearFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFile(null);
        setPreviewData([]);
        setCsvParsed(false);
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        setloading(true);
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const shipment_type = "forword";

        if (file) {
            const formData = new FormData();
            formData.append('csv_file', file);
            formData.append("shipment_type", shipment_type);

            try {
                const response = await axios.post(Base_Url + csvuplode, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'token': token,
                        'cus_id': cus_id,
                    },
                });

                event.target.reset();

                if (response.data.status === "success") {
                    toast.success(response.data.message || "File uploaded successfully!");
                    setReqBody(response.data);
                    navigate("/shipments/all");
                    handleClose();
                } else {
                    if (response.data.errors && Array.isArray(response.data.errors)) {
                        response.data.errors.forEach((err) => toast.error(err));
                    } else {
                        toast.error(response.data.message || "File upload failed.");
                    }
                }
            } catch (error) {
                toast.error("Error uploading file: " + error.message);
            } finally {
                setloading(false);
            }
        }
    };


    const PreviewModal = () => {
        if (!previewData || previewData.length === 0) return null;

        const headers = Object.keys(previewData[0]);

        return createPortal(
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/75 backdrop-blur-md p-3 sm:p-6"
                    onClick={closePreviewModal}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 15, scale: 0.96 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="bg-white dark:bg-[#0b0b16] rounded-2xl sm:rounded-3xl shadow-2xl dark:shadow-[0_25px_80px_rgba(0,0,0,0.95)] w-[96vw] max-w-7xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-200/80 dark:border-indigo-500/30"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-white/15 backdrop-blur-md rounded-xl border border-white/20">
                                    <FileText className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base sm:text-lg font-bold text-white tracking-tight leading-tight">
                                            B2C Bulk Orders Preview
                                        </h2>
                                        <span className="px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-semibold text-white">
                                            {previewData.length} records
                                        </span>
                                    </div>
                                    <p className="text-purple-100 text-xs leading-tight mt-0.5">
                                        Verify your CSV data before creating bulk orders
                                    </p>
                                </div>
                            </div>

                            <button
                                onClick={closePreviewModal}
                                className="p-1.5 bg-white/15 hover:bg-white/25 active:scale-95 rounded-full transition-all text-white"
                                title="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Table Area */}
                        <div className="overflow-auto flex-1 p-3 sm:p-4 bg-slate-50/60 dark:bg-[#070711]">
                            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-indigo-500/25 bg-white dark:bg-[#0c0c18] shadow-sm">
                                <div className="overflow-x-auto max-h-[66vh]">
                                    <table className="w-full text-xs text-left border-collapse">
                                        <thead className="sticky top-0 z-10 bg-slate-100/95 dark:bg-[#121226]/95 backdrop-blur border-b border-gray-200 dark:border-indigo-500/30">
                                            <tr>
                                                <th className="px-3 py-3 text-center font-bold text-gray-500 dark:text-indigo-300 text-[11px] uppercase tracking-wider w-12 border-r border-gray-200 dark:border-indigo-500/20">
                                                    #
                                                </th>
                                                {headers.map((header, index) => (
                                                    <th
                                                        key={index}
                                                        className="px-3.5 py-3 font-bold text-indigo-800 dark:text-indigo-200 text-[11px] uppercase tracking-wider border-r border-gray-200 dark:border-indigo-500/20 last:border-r-0 whitespace-nowrap"
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-indigo-500/15">
                                            {previewData.map((row, rowIndex) => (
                                                <tr
                                                    key={rowIndex}
                                                    className={`${
                                                        rowIndex % 2 === 0 ? 'bg-white dark:bg-[#0b0b14]' : 'bg-slate-50/70 dark:bg-[#0f0f1e]'
                                                    } hover:bg-indigo-50/70 dark:hover:bg-indigo-500/15 transition-colors`}
                                                >
                                                    <td className="px-3 py-3 text-center font-bold text-gray-400 dark:text-indigo-300/60 border-r border-gray-100 dark:border-indigo-500/15">
                                                        {rowIndex + 1}
                                                    </td>
                                                    {headers.map((header, colIndex) => (
                                                        <td
                                                            key={colIndex}
                                                            className="px-3.5 py-3 border-r border-gray-100 dark:border-indigo-500/15 last:border-r-0 align-top max-w-xs truncate"
                                                            title={String(row[header] ?? '')}
                                                        >
                                                            {String(row[header] ?? '')}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-[#0c0c17] border-t border-gray-100 dark:border-indigo-500/20">
                            <span className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-indigo-200/80">
                                <CheckCircle className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                                <span>
                                    Showing <strong className="text-gray-900 dark:text-white">{previewData.length}</strong> rows • {headers.length} columns loaded
                                </span>
                            </span>
                            <button
                                onClick={closePreviewModal}
                                className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-95 text-white rounded-xl font-bold text-xs shadow-md shadow-indigo-500/25 transition-all"
                            >
                                Close Preview
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>,
            document.body
        );
    };

    if (!isopen) return null;

    return (
        <Dialog
            open={isopen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: isDarkMode
                            ? 'rgba(0, 0, 0, 0.8)'
                            : 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                    }
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: '26px',
                    backgroundColor: isDarkMode ? '#0c0c18' : '#ffffff',
                    backgroundImage: isDarkMode
                        ? 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 60%), linear-gradient(135deg, #0b0b14 0%, #0d0d18 50%, #0b0b14 100%)'
                        : 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 60%), none',
                    boxShadow: isDarkMode
                        ? '0 30px 100px -15px rgba(0, 0, 0, 0.95), 0 0 0 1px rgba(99, 102, 241, 0.35), 0 0 50px rgba(79, 70, 229, 0.25)'
                        : '0 25px 70px -10px rgba(79, 70, 229, 0.25), 0 0 0 1px rgba(99, 102, 241, 0.15)',
                    overflow: 'hidden',
                    border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.35)' : 'none',
                    maxWidth: '520px',
                    width: '100%',
                }
            }}
        >
            <DialogTitle
                sx={{
                    p: 0,
                    m: 0,
                    position: 'relative',
                }}
            >
                <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white px-6 py-5">
                    {/* Ambient glow lights */}
                    <div className="absolute -top-10 -right-10 w-36 h-36 bg-purple-400/25 rounded-full blur-2xl pointer-events-none" />
                    <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-indigo-400/25 rounded-full blur-2xl pointer-events-none" />

                    <div className="flex items-center justify-between gap-3 relative z-20">
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/25 flex items-center justify-center shrink-0">
                                <FiUploadCloud className="w-6 h-6 text-white" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-lg font-bold text-white tracking-tight leading-tight">Bulk Order Upload</h2>
                                    <span className="px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-bold text-white tracking-wide uppercase">
                                        B2C Forward
                                    </span>
                                </div>
                                <p className="text-purple-100 text-xs leading-snug mt-0.5">
                                    Import CSV to generate multiple shipments at once
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCloseBulk();
                            }}
                            className="relative z-30 shrink-0 w-8 h-8 bg-white/15 hover:bg-white/30 active:scale-90 rounded-full flex items-center justify-center transition-all text-white backdrop-blur-sm border border-white/25 shadow-sm cursor-pointer"
                            title="Close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4 pointer-events-none" />
                        </button>
                    </div>
                </div>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: isDarkMode ? '#0b0b14' : '#ffffff',
                    padding: '24px !important',
                    '&::-webkit-scrollbar': { width: '5px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? 'rgba(99, 102, 241, 0.5)' : '#c4b5fd',
                        borderRadius: '3px'
                    },
                }}
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Upload Drop Zone */}
                    {!file ? (
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('dropzone-file-b2c')?.click()}
                            className={`group relative flex flex-col items-center justify-center text-center p-7 sm:p-8 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-200 ${
                                isDragging
                                    ? 'border-indigo-500 bg-indigo-500/15 scale-[1.01] shadow-lg shadow-indigo-500/20'
                                    : isDarkMode
                                        ? 'border-indigo-400/40 bg-indigo-500/[0.04] hover:bg-indigo-500/[0.08] hover:border-indigo-400/80'
                                        : 'border-purple-200 bg-gradient-to-b from-purple-50/50 to-indigo-50/30 hover:border-indigo-400 hover:bg-purple-50/80 shadow-sm'
                            }`}
                        >
                            <input
                                id="dropzone-file-b2c"
                                type="file"
                                className="hidden"
                                accept=".csv"
                                onChange={handleFileChange}
                            />

                            <div className="relative mb-3">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/30 group-hover:scale-110 group-hover:shadow-indigo-500/50 transition-all duration-300">
                                    <FiUploadCloud className="w-7 h-7" />
                                </div>
                                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white dark:border-[#0b0b14] animate-ping" />
                                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white dark:border-[#0b0b14]" />
                            </div>

                            <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Drop CSV here, or <span className="text-indigo-600 dark:text-indigo-400 underline underline-offset-2">browse files</span>
                            </p>
                            <p className="text-xs text-gray-400 dark:text-indigo-200/60 mt-1 max-w-xs">
                                Quickly import multiple orders with receiver and parcel details
                            </p>

                            <div className="mt-3.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200/60 dark:border-indigo-500/30 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
                                <span>📄 .CSV format only • Max 10MB</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Selected File Card */}
                            <div className="p-4 rounded-2xl border border-emerald-300 dark:border-emerald-500/40 bg-gradient-to-r from-emerald-50/80 via-teal-50/40 to-emerald-50/80 dark:from-emerald-950/20 dark:via-teal-950/10 dark:to-emerald-950/20 shadow-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/30 shrink-0">
                                            <CheckCircle className="w-6 h-6" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">
                                                {file.name}
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300 font-medium mt-0.5">
                                                <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                                <span>•</span>
                                                <span>{previewData.length} records ready</span>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleClearFile}
                                        className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shrink-0"
                                        title="Remove file"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Preview & Change Actions */}
                                <div className="mt-3 pt-3 border-t border-emerald-200/60 dark:border-emerald-500/20 flex gap-2">
                                    {csvParsed && previewData.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handlePreviewClick}
                                            className="flex-1 flex items-center justify-center gap-2 py-2 px-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-500/25 active:scale-95 transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                            Preview {previewData.length} Rows in Table
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('dropzone-file-b2c')?.click()}
                                        className="px-3.5 py-2 rounded-xl border border-gray-200 dark:border-indigo-500/30 text-gray-700 dark:text-gray-200 text-xs font-semibold hover:bg-gray-100 dark:hover:bg-indigo-500/15 transition-all"
                                    >
                                        Change File
                                    </button>
                                    <input
                                        id="dropzone-file-b2c"
                                        type="file"
                                        className="hidden"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={!file || loading}
                        className={`w-full py-3.5 px-5 font-bold text-sm rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
                            !file || loading
                                ? isDarkMode
                                    ? 'bg-indigo-500/10 text-indigo-300/40 border border-indigo-500/25 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white shadow-lg shadow-indigo-500/30 active:scale-[0.98]'
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <Spinner /> Creating Orders...
                            </span>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                <span>
                                    {file && previewData.length > 0
                                        ? `Create ${previewData.length} Bulk Orders Now`
                                        : 'Create Bulk Orders'}
                                </span>
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </>
                        )}
                    </button>
                </form>

                {/* Sample Template Section */}
                <div className="mt-4 flex items-center justify-between p-3.5 bg-gradient-to-r from-purple-50/70 to-indigo-50/70 dark:from-indigo-950/20 dark:to-purple-950/20 dark:border dark:border-indigo-500/25 rounded-2xl border border-purple-100/80 shadow-sm">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-800 dark:text-gray-100 leading-tight">Sample Format</p>
                            <p className="text-[11px] text-gray-500 dark:text-indigo-300/70 leading-tight mt-0.5">Need a reference template?</p>
                        </div>
                    </div>

                    <a
                        href="/sampleFile/Sample.csv"
                        download="Sample.csv"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-indigo-500/20 hover:bg-indigo-50 dark:hover:bg-indigo-500/30 text-indigo-600 dark:text-indigo-300 rounded-xl text-xs font-bold border border-indigo-200 dark:border-indigo-500/30 shadow-sm transition-all active:scale-95 cursor-pointer"
                    >
                        <FiDownload className="w-3.5 h-3.5" />
                        <span>Download CSV</span>
                    </a>
                </div>

                {/* Modals */}
                <ConfirmBulkOrder
                    isopen={isPopupOpen}
                    onClose={handleClose}
                    data={reqBody}
                    file={file}
                />
                {showPreview && <PreviewModal />}
            </DialogContent>
        </Dialog>
    );
};
export default UploadBulk;